import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowLeft, QrCode } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const METHODS = [
  { value: "upi", label: "UPI (any)" },
  { value: "phonepe", label: "PhonePe" },
  { value: "gpay", label: "Google Pay" },
  { value: "paytm", label: "Paytm" },
  { value: "other", label: "Other" },
];

const empty = { title: "", image_url: "", method: "phonepe", order: 0, is_active: true };

export default function AdminPaymentQRs() {
  const { user, loading: authLoading } = useAuth();
  const [qrs, setQRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payment-qrs");
        setQRs(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (authLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <p className="text-muted-foreground">Admin only.</p>;

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (q) => { setEditing(q); setForm({ ...empty, ...q }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/payment-qrs/${editing.id}`, form);
        setQRs((prev) => prev.map((x) => (x.id === data.id ? data : x)));
        toast.success("Updated");
      } else {
        const { data } = await api.post("/payment-qrs", form);
        setQRs((prev) => [...prev, data]);
        toast.success("QR added");
      }
      setOpen(false);
    } catch {
      toast.error("Failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (q) => {
    if (!window.confirm(`Delete "${q.title}"?`)) return;
    try {
      await api.delete(`/payment-qrs/${q.id}`);
      setQRs((prev) => prev.filter((x) => x.id !== q.id));
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const toggleActive = async (q) => {
    try {
      const { data } = await api.put(`/payment-qrs/${q.id}`, { is_active: !q.is_active });
      setQRs((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex items-baseline justify-between border-b-2 border-primary pb-4 mb-8">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2 hover:underline">
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
          <div className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground flex items-center gap-2">
            <QrCode className="w-3 h-3" /> Payment QR Codes
          </div>
          <h1 className="font-kannada-heading text-3xl mt-1">ಪಾವತಿ QR ಕೋಡ್‌ಗಳು</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-none" data-testid="admin-qr-new">
              <Plus className="w-4 h-4 mr-1" /> ಹೊಸ QR
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-none">
            <DialogHeader>
              <DialogTitle className="font-kannada-heading text-2xl">
                {editing ? "Edit QR" : "Add Payment QR"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Title / Label</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" data-testid="qr-title" placeholder="e.g. PhonePe QR — Mehboob" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">QR Image URL</Label>
                <Input required value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="rounded-none mt-1" data-testid="qr-image" placeholder="https://..." />
                {form.image_url && <img src={form.image_url} alt="" className="mt-2 h-40 w-40 object-contain mx-auto rounded-sm border border-border bg-white p-2" />}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Payment method</Label>
                  <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="qr-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {METHODS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Display Order</Label>
                  <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })} className="rounded-none mt-1" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label className="text-xs uppercase tracking-widest">Active (show to public)</Label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">ರದ್ದು</Button>
                <Button type="submit" disabled={saving} className="rounded-none" data-testid="qr-save">
                  {saving ? "..." : "ಉಳಿಸಿ"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : qrs.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center rounded-sm">
          <QrCode className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No payment QR codes yet. Click <b>ಹೊಸ QR</b> to add one.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="qr-grid">
          {qrs.map((q) => (
            <div key={q.id} className="border border-border bg-card p-5 rounded-sm" data-testid={`qr-card-${q.id}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-primary font-bold">{q.method}</div>
                  <div className="font-kannada-body text-sm font-semibold mt-1 line-clamp-1">{q.title}</div>
                </div>
                <button
                  onClick={() => toggleActive(q)}
                  className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-sm ${q.is_active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                  data-testid={`qr-toggle-${q.id}`}
                >
                  {q.is_active ? "Live" : "Off"}
                </button>
              </div>
              <img src={q.image_url} alt="" className="w-full h-56 object-contain rounded-sm bg-white p-2 border border-border" />
              <div className="flex justify-end gap-1 mt-3">
                <Button size="sm" variant="outline" onClick={() => openEdit(q)} className="rounded-none" data-testid={`qr-edit-${q.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => del(q)} className="rounded-none" data-testid={`qr-delete-${q.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-primary" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
